import { useCallback, useEffect, useState } from 'react';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { travelSafetyApi, safetyErrorMessage } from '../../api/travelSafetyApi';
import { bumpFollowingFeedEpoch } from '../social-following/followingFeedRefresh';
import { clearCreatorPageCache } from '../creator/CreatorPage';

/**
 * 格式化文字標籤，若 i18n 鍵值不存在則回退至繁體中文預設文字。
 */
function safeLabel(t, key, fallback) {
  if (typeof t !== 'function') return fallback;
  const value = t(key);
  return value && value !== key ? value : fallback;
}

/**
 * 決定性已封鎖名單管理區塊 (AUTH-SOCIAL-REL-02)。
 * 負責載入與解除已封鎖對象，支援 CAS optimistic locking 與 Snowflake ID 字串安全。
 */
export default function BlockedMembersSection() {
  const { t } = useTravelTranslation();
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unblockBusyId, setUnblockBusyId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadBlocks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const raw = await travelSafetyApi.listBlocks();
      const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
      const active = list
        .filter((row) => (row.status || 'ACTIVE') === 'ACTIVE')
        .map((row) => ({
          blockedMemberId: String(row.blocked_member_id ?? row.blockedMemberId ?? ''),
          rowVersion: Number(row.row_version ?? row.rowVersion ?? 1),
          createTime: row.create_time ?? row.createTime ?? null,
        }))
        .filter((row) => row.blockedMemberId !== '');
      setBlocks(active);
    } catch (cause) {
      const errText = safetyErrorMessage(cause, t, 'travel.safety.loadBlocksFailed') || '載入封鎖名單失敗';
      setError(errText);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadBlocks();
  }, [loadBlocks]);

  async function handleUnblock(blockedMemberId, expectedRowVersion) {
    if (unblockBusyId || !blockedMemberId) return;
    setUnblockBusyId(blockedMemberId);
    setError('');
    setMessage('');
    try {
      await travelSafetyApi.unblockMember(blockedMemberId, expectedRowVersion);
      setBlocks((prev) => prev.filter((b) => b.blockedMemberId !== blockedMemberId));
      setMessage(safeLabel(t, 'travel.safety.memberUnblocked', '已解除封鎖'));
      bumpFollowingFeedEpoch();
      clearCreatorPageCache();
    } catch (cause) {
      const errText = safetyErrorMessage(cause, t, 'travel.safety.unblockFailed') || '解除封鎖失敗，請稍後再試。';
      setError(errText);
    } finally {
      setUnblockBusyId(null);
    }
  }

  return (
    <section className="blocked-members-section" data-testid="blocked-members-section" aria-labelledby="blocked-members-title">
      <h2 id="blocked-members-title" className="blocked-members-section__title">
        {safeLabel(t, 'travel.safety.blockedMembers', '已封鎖名單')}
      </h2>

      {message ? (
        <p className="blocked-members-section__message" role="status" data-testid="blocked-members-message">
          {message}
        </p>
      ) : null}

      {error ? (
        <div className="blocked-members-section__error-wrap" role="alert" data-testid="blocked-members-error">
          <p className="blocked-members-section__error">{error}</p>
          {!loading ? (
            <button
              type="button"
              className="blocked-members-section__retry"
              data-testid="blocked-members-retry"
              onClick={() => loadBlocks()}
            >
              {safeLabel(t, 'travel.common.retry', '再試一次')}
            </button>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <p className="blocked-members-section__loading" data-testid="blocked-members-loading" aria-live="polite">
          {safeLabel(t, 'travel.safety.loadingBlocks', '載入封鎖名單中...')}
        </p>
      ) : blocks.length === 0 && !error ? (
        <p className="blocked-members-section__empty" data-testid="blocked-members-empty">
          {safeLabel(t, 'travel.safety.noBlockedMembers', '目前沒有封鎖的使用者')}
        </p>
      ) : blocks.length > 0 ? (
        <ul className="blocked-members-section__list" data-testid="blocked-members-list">
          {blocks.map(({ blockedMemberId, rowVersion }) => (
            <li key={blockedMemberId} className="blocked-members-section__item" data-testid={`blocked-item-${blockedMemberId}`}>
              <span className="blocked-members-section__id">
                {safeLabel(t, 'travel.safety.memberLabel', '使用者')} {blockedMemberId}
              </span>
              <button
                type="button"
                className="blocked-members-section__unblock-btn"
                data-testid={`unblock-btn-${blockedMemberId}`}
                disabled={unblockBusyId === blockedMemberId}
                onClick={() => handleUnblock(blockedMemberId, rowVersion)}
              >
                {unblockBusyId === blockedMemberId
                  ? safeLabel(t, 'travel.safety.unblocking', '解除中...')
                  : safeLabel(t, 'travel.safety.unblock', '解除封鎖')}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
